/**
 * ClientTimelineTab - vertical timeline of client activity.
 * Embeddable in any client detail page via <ClientTimelineTab clientId={id} />.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FileText, Send, Mail, Upload, StickyNote, Clock, MessageSquare, Shield, RefreshCw,
  Plus, Download, Pencil, Loader2, X, Edit,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import apiClient from '@/services/api/client';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import AddNoteDialog from './AddNoteDialog';

// ==================== Types & Config ====================

type EventType =
  | 'document_generated'
  | 'email_sent'
  | 'email_received'
  | 'document_uploaded'
  | 'note'
  | 'deadline'
  | 'chat'
  | 'compliance_check'
  | 'xero_sync';

interface TimelineEvent {
  id: string;
  eventType: EventType | string;
  title: string;
  description?: string;
  timestamp: string;
  icon?: string;
  color?: string;
  fileUrl?: string;
  fileName?: string;
  isManual?: boolean;
  metadata?: any;
}

const EVENT_CONFIG: Record<string, { icon: React.ComponentType<{ className?: string }>; bg: string; text: string; label: string }> = {
  document_generated: { icon: FileText, bg: 'bg-indigo-500/15', text: 'text-indigo-600', label: 'Document generated' },
  email_sent: { icon: Send, bg: 'bg-blue-500/15', text: 'text-blue-600', label: 'Email sent' },
  email_received: { icon: Mail, bg: 'bg-green-500/15', text: 'text-green-600', label: 'Email received' },
  document_uploaded: { icon: Upload, bg: 'bg-slate-500/15', text: 'text-slate-600', label: 'Document uploaded' },
  note: { icon: StickyNote, bg: 'bg-amber-500/15', text: 'text-amber-600', label: 'Note' },
  deadline: { icon: Clock, bg: 'bg-red-500/15', text: 'text-red-600', label: 'Deadline' },
  chat: { icon: MessageSquare, bg: 'bg-violet-500/15', text: 'text-violet-600', label: 'Chat' },
  compliance_check: { icon: Shield, bg: 'bg-emerald-500/15', text: 'text-emerald-600', label: 'Compliance check' },
  xero_sync: { icon: RefreshCw, bg: 'bg-sky-500/15', text: 'text-sky-600', label: 'Xero sync' },
};

function configFor(eventType: string) {
  return EVENT_CONFIG[eventType] || { icon: FileText, bg: 'bg-muted', text: 'text-muted-foreground', label: eventType };
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' });
  let hours = d.getHours();
  const mins = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${date} ${hours}:${mins}${ampm}`;
}

function dayKey(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Undated';
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  if (isSameDay(d, today)) return 'Today';
  if (isSameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ==================== Component ====================

export interface ClientTimelineTabProps {
  clientId: string;
}

export function ClientTimelineTab({ clientId }: ClientTimelineTabProps) {
  const queryClient = useQueryClient();
  const [addNoteOpen, setAddNoteOpen] = useState(false);
  const [editEvent, setEditEvent] = useState<TimelineEvent | null>(null);

  const { data: events = [], isLoading, error } = useQuery<TimelineEvent[]>({
    queryKey: ['client-timeline', clientId],
    queryFn: async () => {
      try {
        const resp = await apiClient.get(`/clients/${clientId}/timeline`);
        const body = resp.data?.data || resp.data || [];
        const raw: any[] = Array.isArray(body) ? body : (body.events || body.items || []);
        // The API returns snake_case rows (occurred_at, event_type). Normalize
        // to the camelCase shape this component renders, pulling file links out
        // of metadata when present.
        return raw.map((r: any) => {
          const meta = typeof r.metadata === 'string'
            ? (() => { try { return JSON.parse(r.metadata); } catch { return {}; } })()
            : (r.metadata || {});
          return {
            id: r.id,
            eventType: r.eventType || r.event_type,
            title: r.title,
            description: r.description,
            timestamp: r.timestamp || r.occurred_at || r.created_at,
            icon: r.icon,
            color: r.color,
            fileUrl: r.fileUrl ?? meta.fileUrl ?? meta.file_url,
            fileName: r.fileName ?? meta.fileName ?? meta.file_name,
            isManual: r.isManual ?? (r.source_module === 'manual' || (r.eventType || r.event_type) === 'note'),
            metadata: meta,
          } as TimelineEvent;
        });
      } catch {
        return [];
      }
    },
    enabled: !!clientId,
  });

  const deleteMutation = useMutation({
    mutationFn: async (eventId: string) => apiClient.delete(`/clients/${clientId}/timeline/events/${eventId}`),
    onSuccess: () => {
      toast.success('Note deleted');
      queryClient.invalidateQueries({ queryKey: ['client-timeline', clientId] });
    },
    onError: () => toast.error('Failed to delete note'),
  });

  // Group by day
  const grouped = useMemo(() => {
    const groups: Array<{ day: string; items: TimelineEvent[] }> = [];
    const byDay = new Map<string, TimelineEvent[]>();
    for (const ev of events) {
      const key = dayKey(ev.timestamp);
      if (!byDay.has(key)) byDay.set(key, []);
      byDay.get(key)!.push(ev);
    }
    for (const [day, items] of byDay.entries()) {
      groups.push({ day, items });
    }
    return groups;
  }, [events]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Activity Timeline</h3>
        <Button size="sm" onClick={() => setAddNoteOpen(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Note
        </Button>
      </div>

      {isLoading ? (
        <Card><CardContent className="py-6 text-center text-sm text-muted-foreground">Loading timeline…</CardContent></Card>
      ) : events.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-8 text-center">
          <Clock className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm font-medium">No activity yet</p>
          <p className="text-xs text-muted-foreground mt-1">Documents, emails and notes for this client will appear here.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-5">
          {grouped.map(group => (
            <div key={group.day}>
              <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm py-1 mb-2">
                <Badge variant="secondary" className="text-[11px]">{group.day}</Badge>
              </div>
              <div className="relative pl-8 space-y-3">
                <div className="absolute left-[15px] top-0 bottom-0 w-px bg-border" />
                {group.items.map(ev => (
                  <TimelineItem
                    key={ev.id}
                    event={ev}
                    onEdit={() => setEditEvent(ev)}
                    onDelete={() => {
                      if (confirm('Delete this note?')) deleteMutation.mutate(ev.id);
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <AddNoteDialog open={addNoteOpen} onOpenChange={setAddNoteOpen} clientId={clientId} />
      <EditNoteDialog
        event={editEvent}
        clientId={clientId}
        onClose={() => setEditEvent(null)}
      />
    </div>
  );
}

// ==================== Timeline Item ====================

function TimelineItem({ event, onEdit, onDelete }: {
  event: TimelineEvent;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const cfg = configFor(event.eventType);
  const Icon = cfg.icon;
  const isManualNote = event.eventType === 'note' || event.isManual;

  return (
    <div className="relative">
      {/* Left gutter dot */}
      <div className={cn(
        'absolute -left-8 top-2 h-7 w-7 rounded-full flex items-center justify-center ring-2 ring-background',
        cfg.bg, cfg.text
      )}>
        <Icon className="h-3.5 w-3.5" />
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium truncate">{event.title}</p>
                <Badge variant="outline" className={cn('text-[10px]', cfg.text)}>{cfg.label}</Badge>
              </div>
              {event.description && <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap">{event.description}</p>}
              <div className="flex items-center gap-3 mt-1.5 text-[11px] text-muted-foreground">
                <span>{formatTime(event.timestamp)}</span>
                {event.fileUrl && (
                  <a
                    href={event.fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    <FileText className="h-3 w-3" /> View file
                  </a>
                )}
                {event.fileUrl && event.fileName && (
                  <a
                    href={event.fileUrl}
                    download={event.fileName}
                    className="flex items-center gap-1 text-blue-600 hover:underline"
                  >
                    <Download className="h-3 w-3" /> Download
                  </a>
                )}
              </div>
            </div>
            {isManualNote && (
              <div className="flex items-center">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onEdit}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={onDelete}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== Edit Note Dialog (inline) ====================

function EditNoteDialog({ event, clientId, onClose }: {
  event: TimelineEvent | null;
  clientId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (event) {
      setTitle(event.title || '');
      setDescription(event.description || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!event) return;
      const resp = await apiClient.put(`/clients/${clientId}/timeline/events/${event.id}`, {
        title,
        description: description || undefined,
      });
      return resp.data?.data || resp.data;
    },
    onSuccess: () => {
      toast.success('Note updated');
      queryClient.invalidateQueries({ queryKey: ['client-timeline', clientId] });
      onClose();
    },
    onError: () => toast.error('Failed to update note'),
  });

  return (
    <Dialog open={!!event} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit Note</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="en-title">Title</Label>
            <Input id="en-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="en-desc">Description</Label>
            <Textarea id="en-desc" rows={4} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={mutation.isPending}>Cancel</Button>
          <Button onClick={() => mutation.mutate()} disabled={!title.trim() || mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ClientTimelineTab;
